/**
 * buildMovimientoTicketModel.ts — Modelo canónico del comprobante de movimiento (web + térmica).
 */

import { format } from "date-fns";
import { es } from "date-fns/locale";
import type {
  Devolucion,
  Credito,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import {
  ANULACION_SIN_REEMBOLSO_NOTA,
  DEVOLUCION_REF_NOTA,
  DEVOLUCION_STOCK_NOTA,
  movimientoReversalBannerLabel,
  movimientoTicketTipoLinea,
  movimientoTicketTitulo,
  type MovimientoReversalBanner,
} from "../constants/movimientoTicketCopy";
import {
  clienteNombreAbonoCredito,
  isMovimientoAbonoCredito,
} from "./movimientoAbonoCredito";
import { fmtEmp } from "./getEmpleadoNombre";

function formatMethod(method: string): string {
  const m: Record<string, string> = {
    efectivo: "Efectivo",
    nequi: "Nequi",
    daviplata: "Daviplata",
    tarjeta: "Tarjeta",
    transferencia: "Transferencia",
  };
  return m[method] || method.charAt(0).toUpperCase() + method.slice(1);
}

/** Apertura de gaveta: no hay medio de pago (null oculta la fila en el ticket). */
function metodoPagoParaTicket(
  mov: MovimientoCaja,
  ventas: Venta[],
): string | null {
  if (mov.tipo === "abrir_caja") return null;
  return formatMethod(resolveMovimientoMetodoPago(mov, ventas));
}

export interface MovimientoTicketProductoLinea {
  nombre: string;
  cantidad: number;
  total: number;
}

export interface MovimientoTicketCreditoRef {
  ventaId: string;
  ventaLabel: string;
  valorVenta: number;
  abonadoMovimiento: number;
  saldoPendiente: number;
  estado: "Crédito parcial" | "Crédito saldado" | "Crédito";
}

export interface MovimientoTicketModel {
  movimiento: MovimientoCaja;
  titulo: string;
  tipoLinea: string;
  esAbonoCredito: boolean;
  bannerReversal: MovimientoReversalBanner | null;
  bannerReversalText: string | null;
  monto: number;
  fechaDetalle: string;
  horaDetalle: string;
  registradoPor: string;
  metodoPago: string | null;
  caja: string | null;
  autorizacion: string | null;
  soporte: string | null;
  motivo: string;
  idCorto: string;
  devolucionRef: { id: string; motivo: string } | null;
  devolucionNota: string | null;
  productosDevueltos: MovimientoTicketProductoLinea[];
  stockRestauradoNota: string | null;
  anulacionProductos: MovimientoTicketProductoLinea[];
  anulacionNota: string | null;
  creditoRef: MovimientoTicketCreditoRef | null;
  clienteAbono: string | null;
  movimientoAnulado: boolean;
  motivoAnulacionMov: string | null;
  anuladoPorMov: string | null;
  anuladoEnMov: string | null;
}

function sumDevoluciones(venta: Venta): number {
  return (venta.devoluciones ?? []).reduce(
    (s, d) => s + (d.montoReembolsado || 0),
    0,
  );
}

function sumAnulaciones(venta: Venta): number {
  return (venta.anulaciones ?? []).reduce(
    (s, a) => s + (a.montoAnulado || 0),
    0,
  );
}

function saldoVigenteVenta(venta: Venta): number {
  if (venta.estado === "anulada") return 0;
  const dev = sumDevoluciones(venta);
  const anu = sumAnulaciones(venta);
  return Math.max(0, (venta.total || 0) - dev - anu);
}

import {
  findVentaForMovimiento,
  parseDevolucionMotivo,
} from "./resolveMovimientoCajaLink";
import { resolveMovimientoPuntoCajaNombre } from "./resolveMovimientoPuntoCajaNombre";
import { resolveMovimientoMetodoPago } from "./resolveMovimientoMetodoPago";

function findDevolucion(
  mov: MovimientoCaja,
  ventas: Venta[],
): Devolucion | undefined {
  const parsed = parseDevolucionMotivo(mov.motivo || "");
  if (!parsed) return undefined;
  for (const v of ventas) {
    const d = (v.devoluciones ?? []).find((x) => x.id === parsed.devId);
    if (d) return d;
  }
  return undefined;
}

function resolveDevolucionBanner(
  venta: Venta | undefined,
): MovimientoReversalBanner {
  if (!venta) return "DEVOLUCION";
  const saldo = saldoVigenteVenta(venta);
  const huboDev = (venta.devoluciones?.length ?? 0) > 0;
  if (huboDev && saldo > 0) return "DEVOLUCION_PARCIAL";
  return "DEVOLUCION";
}

function resolveAnulacionBanner(
  mov: MovimientoCaja,
  venta: Venta | undefined,
): MovimientoReversalBanner {
  if (/anulación parcial/i.test(mov.motivo || "")) return "ANULACION_PARCIAL";
  if (venta?.estado === "anulada") return "VENTA_ANULADA";
  if (venta && sumAnulaciones(venta) > 0 && saldoVigenteVenta(venta) > 0) {
    return "ANULACION_PARCIAL";
  }
  return "VENTA_ANULADA";
}

function resolveAnulacionProductos(
  mov: MovimientoCaja,
  venta: Venta | undefined,
): MovimientoTicketProductoLinea[] {
  if (!venta?.anulaciones?.length) return [];
  const ultima = venta.anulaciones[venta.anulaciones.length - 1];
  if (ultima.modo !== "parcial" || !ultima.items?.length) return [];
  return ultima.items.map((it) => ({
    nombre: it.nombre || "Producto",
    cantidad: it.cantidad ?? 1,
    total: (it.precioUnitario ?? 0) * (it.cantidad ?? 1),
  }));
}

function resolveCreditoRef(
  mov: MovimientoCaja,
  creditos: Credito[],
  ventas: Venta[],
): MovimientoTicketCreditoRef | null {
  if (!isMovimientoAbonoCredito(mov)) return null;
  const credito =
    creditos.find((f) => f.id === mov.creditoId) ??
    creditos.find((f) =>
      (f.abonos ?? []).some(
        (a) => Math.round(a.monto) === Math.round(mov.monto),
      ),
    );
  const ventaId =
    ventas.find(
      (v) =>
        v.clienteCreditoNombre === clienteNombreAbonoCredito(mov.motivo || ""),
    )?.id ?? "—";
  const valorVenta = credito?.total ?? mov.monto;
  const abonadoTotal =
    credito?.abonado ??
    (credito?.abonos ?? []).reduce((s, a) => s + (a.monto || 0), 0);
  const saldo =
    credito?.saldoPendiente ?? Math.max(0, valorVenta - abonadoTotal);
  const estado: MovimientoTicketCreditoRef["estado"] =
    saldo <= 0
      ? "Crédito saldado"
      : saldo < valorVenta
        ? "Crédito parcial"
        : "Crédito";
  const ventaLabel =
    ventaId.startsWith("T-") || ventaId.startsWith("v-")
      ? ventaId
      : ventaId.slice(0, 14);
  return {
    ventaId,
    ventaLabel,
    valorVenta,
    abonadoMovimiento: mov.monto,
    saldoPendiente: saldo,
    estado,
  };
}

export interface BuildMovimientoTicketModelInput {
  movimiento: MovimientoCaja;
  ventas?: Venta[];
  creditos?: Credito[];
}

export function buildMovimientoTicketModel(
  input: BuildMovimientoTicketModelInput,
): MovimientoTicketModel {
  const mov = input.movimiento;
  const ventas = input.ventas ?? [];
  const creditos = input.creditos ?? [];
  const venta = findVentaForMovimiento(mov, ventas);
  const esAbonoCredito = isMovimientoAbonoCredito(mov);

  let bannerReversal: MovimientoReversalBanner | null = null;
  if (mov.tipo === "devolucion") {
    bannerReversal = resolveDevolucionBanner(venta);
  } else if (mov.tipo === "anulacion") {
    bannerReversal = resolveAnulacionBanner(mov, venta);
  }

  const devParsed = parseDevolucionMotivo(mov.motivo || "");
  const devolucionReal = findDevolucion(mov, ventas);

  const registradoPor = mov.userName?.trim()
    ? fmtEmp(mov.userName.trim())
    : mov.usuarioId?.trim()
      ? `Usuario · ${mov.usuarioId.slice(0, 14)}`
      : "No indicado";

  let autorizacion: string | null = null;
  if (mov.authorizationType) {
    autorizacion =
      mov.authorizationType === "propia"
        ? "Propia"
        : `Por ${mov.authorizedBy || "administrador"}`;
  }

  let soporte: string | null = null;
  if (mov.tipoSoporte) {
    soporte =
      mov.tipoSoporte === "con_factura"
        ? "Con factura"
        : mov.tipoSoporte === "no_verificado"
          ? "No verificado"
          : "Sin soporte";
  }

  const productosDevueltos: MovimientoTicketProductoLinea[] =
    devolucionReal?.items?.map((it) => ({
      nombre: it.nombre,
      cantidad: it.cantidad,
      total: it.precioUnitario * it.cantidad,
    })) ?? [];

  const anulacionProductos = resolveAnulacionProductos(mov, venta);

  const creditoRef = resolveCreditoRef(mov, creditos, ventas);
  const clienteAbono = esAbonoCredito
    ? clienteNombreAbonoCredito(mov.motivo || "")
    : null;

  const fecha = new Date(mov.fecha);

  return {
    movimiento: mov,
    titulo: movimientoTicketTitulo(mov),
    tipoLinea: movimientoTicketTipoLinea(mov),
    esAbonoCredito,
    bannerReversal,
    bannerReversalText: bannerReversal
      ? movimientoReversalBannerLabel(bannerReversal)
      : null,
    monto: mov.monto,
    fechaDetalle: format(fecha, "d MMM yyyy", { locale: es }),
    horaDetalle: format(fecha, "HH:mm"),
    registradoPor,
    metodoPago: metodoPagoParaTicket(mov, ventas),
    caja: resolveMovimientoPuntoCajaNombre(mov),
    autorizacion,
    soporte,
    motivo: mov.motivo?.trim() || "—",
    idCorto: mov.id.slice(0, 14).toUpperCase(),
    devolucionRef: devParsed
      ? { id: devParsed.devId, motivo: devParsed.motivoCorto }
      : null,
    devolucionNota: mov.tipo === "devolucion" ? DEVOLUCION_REF_NOTA : null,
    productosDevueltos,
    stockRestauradoNota:
      productosDevueltos.length > 0 ? DEVOLUCION_STOCK_NOTA : null,
    anulacionProductos,
    anulacionNota:
      mov.tipo === "anulacion" ? ANULACION_SIN_REEMBOLSO_NOTA : null,
    creditoRef,
    clienteAbono,
    movimientoAnulado: mov.estado === "anulado",
    motivoAnulacionMov: mov.motivoAnulacion?.trim() || null,
    anuladoPorMov: mov.anuladoPor?.trim() || null,
    anuladoEnMov: mov.anuladoEn
      ? format(new Date(mov.anuladoEn), "d MMM yyyy, HH:mm", { locale: es })
      : null,
  };
}
